import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { Observable } from '../util/rxjs';
import { Localized, isLocalized, isLocalizedMoment } from './locale.model';
import { LocaleService } from './locale.service';

const collectTranslatedFields = (model: any): Localized<any>[] => {
  if (isLocalized(model)) {
    return [model];
  }

  return Object.keys(model).reduce((all, key) => {
    const field = model[key];
    if (isLocalized(field)) {
      return [...all, field];
    }
    if (field != null && typeof field === 'object') {
      return [...all, ...collectTranslatedFields(field)];
    }
    return all
  }, [] as Localized<any>[]);
}

const normalizeTranslation = (translation: string | { [key: string]: string }) => {
  if (typeof translation === 'string') {
    return translation;
  }
  return Object.keys(translation)
    .reduce((values, key) => [...values, translation[key]], [] as string[])
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
}

@Injectable()
export class LocalizedModelService {
  constructor(private translateService: TranslateService, private localeService: LocaleService) {}

  stream<T extends object>(model: T): Observable<T> {
    return Observable.of(this.translate(model))
      .concat(this.localeService.locale$.map(() => this.translate(model)));
  }

  translate<T extends object>(model: T): T {
    collectTranslatedFields(model)
      .map(field => [field, this.getTranslation(field)])
      .forEach(pair => pair[0].label = normalizeTranslation(pair[1]));
    return model;
  }

  translateAll<T extends object>(model: T[]): T[] {
    return model.map(value => this.translate(value));
  }

  private getTranslation(field: Localized<any>) {
    if (isLocalizedMoment(field)) {
      return this.localeService.formatMoment(field.value, field.localizationKey);
    }
    return this.translateService.instant(field.localizationKey, this.prepareLocalizationParams(field.localizationParams));
  }

  private prepareLocalizationParams(params: { [key: string]: any }) {
    if (!params) {
      return params;
    }

    const translatedParams = this.translate(params);
    return Object.keys(translatedParams)
      .reduce((newParams, paramName) => {
        const translatedParam = translatedParams[paramName];
        newParams[paramName] = isLocalized(translatedParam) ? translatedParam.label : translatedParam;
        return newParams;
      }, {} as { [key: string]: string });
  }
}

